import { AuthGuard, CheckPermissionsModes } from "@elit-hair/elit-tools";
import { DatePicker } from "@elit-hair/elit-ui";
import { Outlet, createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { DateTime } from "luxon";
import { useCallback } from "react";
import { z } from "zod";

import { PERMISSIONS } from "@/constants/permissions";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

import { ChartPeriodSelector } from "./components/quality-watch-chart-priod-selector";
import { QualityWatchProvider, useQualityWatch } from "./hooks/use-quality-watch";
import { CALL_PURPOSES, CHART_LAYOUT_TYPES, CHART_PERIODS } from "./utils/quality-watch.constants";

const EntitySchema = z.object({
    id: z.string(),
    type: z.string(),
    relationId: z.string(),
    name: z.string(),
    hidden: z.boolean().optional(),
});

const QualityWatchSearchSchema = z.object({
    d: z.string().optional(),
    p: z
        .enum([CHART_PERIODS.DAILY, CHART_PERIODS.WEEKLY, CHART_PERIODS.MONTHLY, CHART_PERIODS.TWENTY_FOUR_HOURS])
        .optional(),
    def: z.string().optional(),
    e: z.array(EntitySchema).optional(),
    layout: z.enum([CHART_LAYOUT_TYPES.COMBINED, CHART_LAYOUT_TYPES.SEPARATE]).optional(),
    cp: z.array(z.nativeEnum(CALL_PURPOSES)).optional(),
});

export type QualityWatchSearch = z.infer<typeof QualityWatchSearchSchema>;

export const Route = createFileRoute(APP_ROUTES_FULL.PERFORMANCE_QUALITY_WATCH)({
    component: PerformanceQualityWatchLayout,
    validateSearch: zodValidator(QualityWatchSearchSchema),
    staticData: {
        config: {
            breadcrumb: {
                title: "quality_watch",
                to: APP_ROUTES_TO.PERFORMANCE_QUALITY_WATCH,
            },
            title: "quality_watch",
        },
        permissions: [PERMISSIONS.CALL_SVC_TEAM_LEAD_BASE],
    },
});

function PerformanceQualityWatchLayout() {
    return (
        <AuthGuard
            required={[PERMISSIONS.CALL_SVC_TEAM_LEAD_BASE, PERMISSIONS.CALL_SVC_ADMIN_BASE]}
            permissionMode={CheckPermissionsModes.ANY}
            fallback={null}
        >
            <QualityWatchProvider>
                <QualityWatchLayoutContent />
            </QualityWatchProvider>
        </AuthGuard>
    );
}

function QualityWatchLayoutContent() {
    const { period, setPeriod, selectedDate, setSelectedDate } = useQualityWatch();

    const handleDateChange = useCallback(
        (date: Date | undefined) => {
            if (date) setSelectedDate(DateTime.fromJSDate(date));
        },
        [setSelectedDate]
    );

    return (
        <div className="flex flex-col gap-4 p-2 h-full w-full relative">
            <div className="absolute top-0 right-2 flex items-center gap-2">
                <DatePicker date={selectedDate.toJSDate()} onDateChange={handleDateChange} />
                <ChartPeriodSelector period={period} setPeriod={setPeriod} />
            </div>

            <div className="h-full w-full pt-4">
                <Outlet />
            </div>
        </div>
    );
}
